// TimeControls — pause/play + speed selector, docked in the TopBar.
//
// Pure UI: state + actions come from useSimWorld. Each speed button is
// active when `props.speed === button.value`. The pause/play toggle reflects
// `props.runState`. Below `sm` the three speed buttons collapse into one
// button that cycles 1× → 2× → 4× so the bar fits a 390px phone.

import { Button } from '@aod/ui/components/ui/button';
import { cn } from '@aod/ui/lib/utils';
import { Pause, Play } from 'lucide-react';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { type SpeedOption, allowedSpeeds } from '../setup/clientAssists.js';

interface TimeControlsProps {
  readonly runState: 'running' | 'paused' | 'stopped';
  readonly speed: number;
  readonly onPause: () => void;
  readonly onResume: () => void;
  readonly onSetSpeed: (multiplier: number) => void;
  /** Highest speed offered (the human seat's `speedCap` assist). Default 4×. */
  readonly maxSpeed?: number;
  readonly className?: string;
}

export function nextSpeed(current: number, maxSpeed = 4): number {
  const options = allowedSpeeds(maxSpeed);
  const i = options.indexOf(current as SpeedOption);
  return options[(i + 1) % options.length] ?? 1;
}

export function TimeControls({
  runState,
  speed,
  onPause,
  onResume,
  onSetSpeed,
  maxSpeed = 4,
  className,
}: TimeControlsProps) {
  const t = useT();
  const paused = runState === 'paused';
  const speedOptions = allowedSpeeds(maxSpeed);

  return (
    <fieldset
      data-testid="time-controls"
      {...appDom({
        id: 'time-controls',
        type: 'Toggle',
        role: 'container',
        scope: 'header',
        label: 'Time controls',
      })}
      aria-label={t('time.label')}
      className={cn(
        'm-0 flex min-w-0 items-center gap-0.5 rounded-md border border-border bg-muted/60 p-0.5',
        className,
      )}
    >
      <Button
        type="button"
        size="icon"
        variant={paused ? 'default' : 'ghost'}
        aria-label={paused ? t('time.resume') : t('time.pause')}
        title={paused ? t('time.resume') : t('time.pause')}
        aria-pressed={paused}
        onClick={paused ? onResume : onPause}
        data-testid="time-play-pause"
        {...appDom({ id: 'time-play-pause', type: 'IconButton', role: 'button', scope: 'header' })}
        className="size-7"
      >
        {paused ? <Play className="size-3.5" /> : <Pause className="size-3.5" />}
      </Button>
      {speedOptions.map((value) => (
        <Button
          key={value}
          type="button"
          size="sm"
          variant={speed === value ? 'secondary' : 'ghost'}
          aria-label={t('time.setSpeed', { speed: value })}
          aria-pressed={speed === value}
          onClick={() => onSetSpeed(value)}
          data-testid={`time-speed-${value}`}
          {...appDom({
            id: `time-speed-${value}`,
            type: 'Button',
            role: 'button',
            scope: 'header',
          })}
          className={cn(
            'hidden h-7 min-w-8 px-2 font-mono text-[11px] tabular-nums tracking-wide sm:inline-flex',
            speed === value && 'bg-background text-foreground shadow-sm ring-1 ring-border',
          )}
        >
          {value}×
        </Button>
      ))}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        data-testid="speed-cycle"
        {...appDom({ id: 'speed-cycle', type: 'Button', role: 'button', scope: 'header' })}
        aria-label={t('time.cycleSpeed', { speed })}
        title={t('time.cycleSpeed', { speed })}
        onClick={() => onSetSpeed(nextSpeed(speed, maxSpeed))}
        className="h-7 min-w-8 px-1.5 font-mono text-[11px] tabular-nums sm:hidden"
      >
        {speed}×
      </Button>
    </fieldset>
  );
}
